import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { WhereUsed } from '../../services/types';

interface PartWhereUsedTableProps {
  whereUsed: WhereUsed[];
}

export const PartWhereUsedTable: React.FC<PartWhereUsedTableProps> = ({
  whereUsed,
}) => {
  const { t } = useTranslation();

  // Filter to only show approved revisions of active parts
  const filteredWhereUsed = whereUsed.filter(
    (item) => !item.partInactive && item.revisionApproved
  );

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      {/* Header */}
      <div className="bg-purple-50 px-4 py-3 border-b border-purple-200 flex-shrink-0">
        <h3 className="text-lg font-bold text-purple-900">
          {t('parts.whereUsed.title')}
        </h3>
        <p className="text-xs text-purple-700">
          {filteredWhereUsed.length}{' '}
          {filteredWhereUsed.length === 1
            ? t('parts.records')
            : t('parts.recordPlural')}
        </p>
      </div>

      {/* Content */}
      {filteredWhereUsed.length > 0 ? (
        <div className="overflow-x-auto flex-grow">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.whereUsed.parent')}
                </th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">
                  {t('parts.whereUsed.rev')}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.whereUsed.description')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.whereUsed.qtyPer')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredWhereUsed.map((record, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-900 font-medium whitespace-nowrap">
                    <Link
                      to={`/parts/${encodeURIComponent(record.partNum)}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {record.partNumUM || record.partNum}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-700">
                    {record.revisionNum || '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {record.partDescription}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-medium">
                    {record.qtyPer.toLocaleString()}
                    {record.fixedQty && (
                      <span className="ml-1 text-xs text-amber-600">
                        ({t('parts.whereUsed.fixedQty')})
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4">
          <p className="text-sm text-gray-500 italic">
            {t('parts.whereUsed.noData')}
          </p>
        </div>
      )}
    </div>
  );
};
